import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient } from "@/services/api";
import { showSuccessMessage } from "@/components/common/GlobalSnackbar";
import toast from "react-hot-toast";

interface User {
  id: number;
  username: string;
  display_name?: string;
  email: string;
  role: string;
  organization_name?: string;
  created_at: string;
}

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    username: "",
    display_name: "",
    email: "",
    password: "",
    role: "writer",
  });
  const [error, setError] = useState("");

  // 检查权限
  const canManageUsers =
    currentUser?.role === "admin" || currentUser?.role === "super_admin" || currentUser?.role === "zhaoyuan_admin";

  useEffect(() => {
    if (canManageUsers) {
      loadUsers();
    }
  }, [canManageUsers]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiClient.users.getAll();
      if (response.success) {
        setUsers(response.data || []);
      } else {
        setError(response.message || "加载用户列表失败");
      }
    } catch (err: any) {
      console.error("加载用户列表失败:", err);
      setError(err.message || "加载用户列表失败");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({ username: "", display_name: "", email: "", password: "", role: "writer" });
    setError("");
    setOpenDialog(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      display_name: user.display_name || "",
      email: user.email,
      password: "",
      role: user.role,
    });
    setError("");
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      setError("");
      console.log("开始提交用户数据:", formData);
      
      if (editingUser) {
        // 更新用户
        const updateData: any = {
          email: formData.email,
          display_name: formData.display_name,
          role: formData.role,
        };
        
        // 如果提供了新密码，则更新密码
        if (formData.password) {
          updateData.password = formData.password;
        }
        
        const response = await apiClient.users.update(editingUser.id, updateData);
        if (response.success) {
          showSuccessMessage("用户更新成功");
          await loadUsers(); // 重新加载用户列表
          setOpenDialog(false);
        } else {
          setError(response.message || "更新用户失败");
          toast.error(response.message || "更新用户失败");
        }
      } else {
        // 创建新用户
        const createData = {
          username: formData.username,
          display_name: formData.display_name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
        };
        
        console.log("创建用户数据:", createData);
        try {
          const response = await apiClient.users.create(createData);
          console.log("创建用户响应:", response);
          if (response.success) {
            showSuccessMessage("用户创建成功");
            await loadUsers(); // 重新加载用户列表
            setOpenDialog(false);
          } else {
            console.log("创建用户失败:", response.message);
            setError(response.message || "创建用户失败");
            toast.error(response.message || "创建用户失败");
          }
        } catch (createError: any) {
          console.log("创建用户异常:", createError);
          console.log("异常详情:", createError.response);
          console.log("异常message:", createError.message);
          
          // 提取错误信息的优先级：
          // 1. error.message（经过handleResponseError处理后的错误信息，应该包含detail）
          // 2. response.data.detail（HTTPException的原始格式）
          // 3. response.data.message（正常响应格式）
          // 4. 默认消息
          const errorMessage = createError.message || 
                              createError.response?.data?.detail || 
                              createError.response?.data?.message || 
                              "创建用户失败";
          
          console.log("最终提取的错误信息:", errorMessage);
          setError(errorMessage);
          toast.error(errorMessage);
        }
      }
    } catch (err: any) {
      console.error("操作失败:", err);
      console.log("错误详情:", err);
      const errorMessage = err.response?.data?.detail || err.message || "操作失败";
      console.log("提取的错误信息:", errorMessage);
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  if (!canManageUsers) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">您没有权限访问此页面</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Typography>加载中...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">用户管理</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateUser}
        >
          创建用户
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>用户名</TableCell>
              <TableCell>显示名称</TableCell>
              <TableCell>邮箱</TableCell>
              <TableCell>角色</TableCell>
              <TableCell>组织</TableCell>
              <TableCell>创建时间</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.id}</TableCell>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.display_name || "-"}</TableCell>
                <TableCell>{user.email || "-"}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>{user.organization_name || "-"}</TableCell>
                <TableCell>{user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditUser(user)}
                    sx={{ mr: 1 }}
                  >
                    编辑
                  </Button>
                  <Button size="small" color="error" startIcon={<DeleteIcon />}>
                    删除
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>{editingUser ? "编辑用户" : "创建用户"}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, fontSize: '16px', fontWeight: 'bold' }}>
              {error}
            </Alert>
          )}
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="用户名"
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              sx={{ mb: 2 }}
              disabled={!!editingUser} // 编辑时禁用用户名修改
            />
            <TextField
              fullWidth
              label="显示名称"
              value={formData.display_name}
              onChange={(e) =>
                setFormData({ ...formData, display_name: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="邮箱"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            {!editingUser && (
              <TextField
                fullWidth
                label="密码"
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                sx={{ mb: 2 }}
              />
            )}
            {editingUser && (
              <TextField
                fullWidth
                label="新密码（留空则不修改）"
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                sx={{ mb: 2 }}
              />
            )}
            <FormControl fullWidth>
              <InputLabel>角色</InputLabel>
              <Select
                value={formData.role}
                label="角色"
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value })
                }
              >
                <MenuItem value="writer">写作员</MenuItem>
                <MenuItem value="admin">管理员</MenuItem>
                {currentUser?.role === "super_admin" && (
                  <MenuItem value="super_admin">超级管理员</MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>取消</Button>
          <Button onClick={handleSubmit} variant="contained">
            确定
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
